import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';

const STATUS_COLORS = {
  AVAILABLE: { color: 'text-primary',     bg: 'bg-primary/10' },
  RESERVED:  { color: 'text-secondary',   bg: 'bg-secondary/10' },
  SOLD:      { color: 'text-error-dim',   bg: 'bg-error-dim/10' },
};

function MyProducts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);
  const [statusFilter, setStatusFilter] = useState('ALL');

  const STATUS_CONFIG = {
    AVAILABLE: { label: t('Disponible'), icon: 'check_circle', ...STATUS_COLORS.AVAILABLE },
    RESERVED:  { label: t('Reservado'),  icon: 'pending',      ...STATUS_COLORS.RESERVED },
    SOLD:      { label: t('Vendido'),    icon: 'sell',         ...STATUS_COLORS.SOLD },
  };

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['myProducts'],
    queryFn: async () => {
      const r = await productService.getProductsByCreator(user.id);
      return r.data || [];
    },
    enabled: !!user,
  });

  const metrics = useMemo(() => ({
    total: products.length,
    available: products.filter((p) => p.status === 'AVAILABLE').length,
    reserved: products.filter((p) => p.status === 'RESERVED').length,
    sold: products.filter((p) => p.status === 'SOLD').length,
    views: products.reduce((s, p) => s + (p.viewCount || 0), 0),
  }), [products]);

  const filtered = statusFilter === 'ALL' ? products : products.filter((p) => p.status === statusFilter);

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/profile" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-6xl mx-auto">

          {/* ── Header ── */}
          <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-end mobile-sm:justify-between gap-6 mb-8">
            <div className="flex items-start mobile-sm:items-center gap-4">
              <a
                onClick={() => navigate('/profile')}
                className="p-2 rounded-xl hover:bg-surface-container-highest cursor-pointer transition-colors"
              >
                <span className="material-symbols-outlined text-on-surface-variant">arrow_back</span>
              </a>
              <div>
                <h1 className="text-3xl font-black text-on-surface tracking-tight mb-1">{t('Mis Productos')}</h1>
                <p className="text-on-surface-variant text-sm font-medium">{t('Todas tus publicaciones en un solo lugar')}</p>
              </div>
            </div>
            <div className="w-full mobile-sm:w-auto">
              {/* Mobile: 3 cuadrados uno al lado del otro */}
              <div className="grid grid-cols-3 gap-2 mobile-sm:hidden">
                <div className="bg-surface-container rounded-xl p-3 flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold leading-tight text-center">{t('Disponible')}</span>
                  <span className="text-2xl font-black text-primary leading-none mt-1">{metrics.available}</span>
                </div>
                <div className="bg-surface-container rounded-xl p-3 flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold leading-tight text-center">{t('Reservado')}</span>
                  <span className="text-2xl font-black text-secondary leading-none mt-1">{metrics.reserved}</span>
                </div>
                <div className="bg-surface-container rounded-xl p-3 flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold leading-tight text-center">{t('Vendido')}</span>
                  <span className="text-2xl font-black text-error-dim leading-none mt-1">{metrics.sold}</span>
                </div>
              </div>
              {/* Desktop: barra horizontal con divisores */}
              <div className="hidden mobile-sm:flex bg-surface-container px-5 py-3 rounded-xl items-center gap-4">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Disponible')}</span>
                  <span className="text-xl font-black text-primary">{metrics.available}</span>
                </div>
                <div className="w-[1px] h-8 bg-outline-variant/20" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Reservado')}</span>
                  <span className="text-xl font-black text-secondary">{metrics.reserved}</span>
                </div>
                <div className="w-[1px] h-8 bg-outline-variant/20" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Vendido')}</span>
                  <span className="text-xl font-black text-error-dim">{metrics.sold}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Filter Bar ── */}
          <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-between gap-3 mb-6 bg-surface border border-outline p-2 rounded-xl">
            <a
              onClick={() => navigate('/create-product')}
              className="w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary-container text-white rounded-xl text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
            >
              <span className="material-symbols-outlined text-sm">add_circle</span>
              {t('Publicar')}
            </a>
            <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-2 mobile-sm:gap-3 px-2 mobile-sm:px-4">
              <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                <span className="material-symbols-outlined text-base">filter_list</span>
                <span>{t('Filtrar')}:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 py-0 cursor-pointer"
              >
                <option value="ALL">{t('Todos')} ({metrics.total})</option>
                <option value="AVAILABLE">{t('Disponible')} ({metrics.available})</option>
                <option value="RESERVED">{t('Reservado')} ({metrics.reserved})</option>
                <option value="SOLD">{t('Vendido')} ({metrics.sold})</option>
              </select>
            </div>
          </div>

          {/* ── Product List ── */}
          <div className="bg-surface border border-outline rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="hidden tablet-sm:grid grid-cols-[48px_1fr_100px_100px_80px] gap-4 px-6 py-3 border-b border-outline-variant/10 text-[10px] uppercase tracking-widest font-black text-on-surface-variant/60">
              <span>#</span>
              <span>{t('Producto')}</span>
              <span>{t('Precio')}</span>
              <span>{t('Estado')}</span>
              <span className="text-right">{t('Acción')}</span>
            </div>

            {isLoading ? (
              <>
                <div className="tablet-sm:hidden divide-y divide-outline-variant/10">
                  {[1, 2, 3].map((i) => (
                    <div key={`mobile-${i}`} className="p-4 animate-pulse space-y-3">
                      <div className="h-40 rounded-xl bg-surface-container-highest" />
                      <div className="h-4 rounded bg-surface-container-highest w-2/3" />
                      <div className="h-3 rounded bg-surface-container-highest w-1/3" />
                    </div>
                  ))}
                </div>
                <div className="hidden tablet-sm:block divide-y divide-outline-variant/10">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="grid grid-cols-[48px_1fr_100px_100px_80px] gap-4 px-6 py-3 items-center animate-pulse">
                    <div className="h-3 bg-surface-container-highest rounded w-6" />
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-surface-container-highest" />
                      <div className="h-3 bg-surface-container-highest rounded w-32" />
                    </div>
                    <div className="h-3 bg-surface-container-highest rounded w-16" />
                    <div className="h-3 bg-surface-container-highest rounded w-16" />
                    <div />
                  </div>
                ))}
                </div>
              </>
            ) : filtered.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">storefront</span>
                <p className="text-sm font-bold text-on-surface mb-1">{t('No se encontraron productos')}</p>
                <p className="text-xs text-on-surface-variant mb-4">
                  {statusFilter === 'ALL' ? t('Publica tu primer producto para empezar a vender.') : t('No hay productos con este estado.')}
                </p>
                {statusFilter === 'ALL' && (
                  <a
                    onClick={() => navigate('/create-product')}
                    className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    {t('Publicar')}
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="tablet-sm:hidden grid grid-cols-2 gap-3 mobile-sm:gap-4 p-4">
                  {filtered.map((product) => {
                    const imageUrl = getImageUrl(product.imageId);
                    const status = STATUS_CONFIG[product.status] || STATUS_CONFIG.AVAILABLE;

                    return (
                      <div
                        key={`mobile-${product.id}`}
                        className="rounded-xl bg-surface border border-outline p-3 shadow-sm cursor-pointer"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        <div className="aspect-[4/3] rounded-xl overflow-hidden bg-surface-container-highest mb-3">
                          {imageUrl ? (
                            <img className="w-full h-full object-cover" src={imageUrl} alt="" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="material-symbols-outlined text-3xl text-on-surface-variant">shopping_bag</span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-on-surface truncate">{product.name}</p>
                              {product.communityName && (
                                <p className="text-[11px] text-on-surface-variant truncate">{product.communityName}</p>
                              )}
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${status.bg} ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center text-sm">
                            <span className="font-semibold text-on-surface">
                              {product.price != null ? `$${product.price}` : '--'}
                            </span>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <a
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/products/${product.id}/edit`);
                              }}
                              className="flex-1 p-2 rounded-lg bg-surface-container-highest text-on-surface-variant text-center cursor-pointer"
                            >
                              <span className="material-symbols-outlined text-sm">edit</span>
                            </a>
                            <a
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/products/${product.id}`);
                              }}
                              className="flex-1 p-2 rounded-lg bg-primary text-on-primary text-center cursor-pointer"
                            >
                              <span className="material-symbols-outlined text-sm">chevron_right</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hidden tablet-sm:block divide-y divide-outline-variant/10">
                  {filtered.map((product, idx) => {
                    const imageUrl = getImageUrl(product.imageId);
                    const status = STATUS_CONFIG[product.status] || STATUS_CONFIG.AVAILABLE;

                    return (
                      <div
                        key={product.id}
                        className="grid grid-cols-[48px_1fr_100px_100px_80px] gap-4 px-6 py-2.5 items-center hover:bg-surface-container transition-colors group cursor-pointer"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        <span className="text-xs font-medium text-on-surface-variant/50">
                          {String(idx + 1).padStart(2, '0')}
                        </span>

                        <div className="flex items-center gap-3">
                          {imageUrl ? (
                            <img className="w-10 h-10 rounded-lg object-cover bg-surface-container-highest shrink-0" src={imageUrl} alt="" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-surface-container-highest flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-sm text-on-surface-variant">shopping_bag</span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="text-sm font-bold text-on-surface truncate block">{product.name}</span>
                            {product.communityName && (
                              <span className="text-[10px] text-on-surface-variant">{product.communityName}</span>
                            )}
                          </div>
                        </div>

                        <span className="text-sm font-semibold text-on-surface">
                          {product.price != null ? `$${product.price}` : '--'}
                        </span>

                        <div className={`flex items-center gap-1.5 ${status.color}`}>
                          <span className="material-symbols-outlined text-sm">{status.icon}</span>
                          <span className="text-[11px] font-bold uppercase tracking-tight">{status.label}</span>
                        </div>

                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <a
                            onClick={() => navigate(`/products/${product.id}/edit`)}
                            className="p-1.5 rounded-lg hover:bg-surface-container-highest cursor-pointer transition-colors opacity-100 tablet-md:opacity-0 tablet-md:group-hover:opacity-100"
                            title="Edit"
                          >
                            <span className="material-symbols-outlined text-sm text-on-surface-variant">edit</span>
                          </a>
                          <a
                            onClick={() => navigate(`/products/${product.id}`)}
                            className="bg-surface-container-highest group-hover:bg-primary group-hover:text-on-primary p-1.5 rounded-lg transition-all cursor-pointer inline-flex"
                          >
                            <span className="material-symbols-outlined text-sm">chevron_right</span>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default MyProducts;
